/**
 * `search` — FR-18, FR-20, FR-25, FR-27, NFR-06, NFR-10.
 *
 * ## What is deliberately absent from the request
 *
 * There is **no** `scope`, `parties_in_scope`, `party_keys_allowed`, `as_user`,
 * `include_all` or `bypass_scope` parameter, and there never will be.
 * Authorisation is resolved server-side from Entra group claims and compiled into
 * the SQL `WHERE` clause, so that *the query is incapable of returning
 * unauthorised rows* (NFR-06). Scope is a query predicate, never a post-filter —
 * and a caller-supplied scope parameter is a post-filter with extra steps,
 * because it can be omitted.
 *
 * Scope resolution also walks **up** `amends_contract_key` before building the
 * predicate, because an amendment inherits its parent's security (§8.1 Rule 4).
 *
 * The request schemas here are `.strict()`. That is load-bearing rather than
 * tidy: an unknown `scope` key must be *rejected*, not silently ignored, so a
 * client that tries to widen its own scope gets a `validation_failed` instead of
 * a result set that looks fine.
 *
 * ## What is deliberately absent from the pipeline
 *
 * No LLM appears in this path. NFR-10 (1–3 s per search is unacceptable), FR-27
 * (an LLM reranker cannot explain itself) and O-6 (the same query must rank the
 * same way, every time). The single permitted exception is optional, cached query
 * expansion — reflected here only as `aliases_applied` on the trace.
 */
import { z } from 'zod';
import { ClauseCodeSchema, ContractKeySchema, IsoDateSchema, PartyKeySchema, UnitScoreSchema, UpnSchema, VersionNoSchema, } from '../primitives.js';
import { ClauseRepresentationSchema, ContractTypeSchema, LifecycleStatusSchema, ProcessingStatusSchema, SearchJudgementSchema, } from '../vocabulary.js';
import { PageInfoSchema, PageRequestSchema } from '../pagination.js';
import { ContractSummarySchema } from '../domain/contract.js';
import { LibraryClauseSchema } from '../domain/clause.js';
import { TagFilterSchema, TagSchema } from './contract-tags.js';
import { RegionSchema } from '../domain/region.js';
import { CloudeqEntityKeySchema } from '../domain/cloudeq-entity.js';
/* -------------------------------------------------------------------------- */
/* Reason trace — FR-27                                                        */
/* -------------------------------------------------------------------------- */
/**
 * One signal that contributed to a result's score.
 *
 * FR-27 requires that *every* result can be explained: which signals contributed
 * and why. WP-09 §2 states the trade-off explicitly — *"explainable over
 * accurate"*: an unexplainable ranking is a worse outcome in this system than a
 * slightly weaker one, because audit-readiness is the product.
 *
 * Each member carries a machine-readable `value` (its contribution to the final
 * score) **and** a human-readable `detail`. The `detail` is generated by the
 * search service, not composed in the front end — it is the compliance artefact,
 * and it has to read the same in the admin UI, in a log and in an evidence pack.
 */
export const ScoreContributionSchema = z.discriminatedUnion('source', [
    z.object({
        /** Full-text rank — `ts_rank` over the `tsvector` GIN index (FR-20). */
        source: z.literal('lexical'),
        /** 1-based rank in the lexical retrieval, before fusion. */
        rank: z.number().int().min(1),
        value: z.number(),
        detail: z.string().min(1).max(500),
        matched_terms: z.array(z.string().min(1).max(120)),
    }),
    z.object({
        /** Cosine over embeddings (FR-25). */
        source: z.literal('vector'),
        rank: z.number().int().min(1),
        value: z.number(),
        detail: z.string().min(1).max(500),
        /**
         * A clause is scored on its **best-matching** representation, not the average
         * — a strong title hit must not be diluted by a poor body match (WP-09 §4).
         * This says which one won. `null` for contract-content hits, which have one
         * representation.
         */
        best_representation: ClauseRepresentationSchema.nullable(),
        cosine: UnitScoreSchema,
    }),
    z.object({
        /** An admin-curated or mined vocabulary entry fired — WP-09 §5 stage ①. */
        source: z.literal('alias'),
        value: z.number(),
        detail: z.string().min(1).max(500),
        alias_term: z.string().min(1).max(120),
        canonical_term: z.string().min(1).max(200),
    }),
    z.object({
        /**
         * k-NN over judged queries — WP-09 §6. This is how a correction *generalises*:
         * judged queries are themselves embedded, so a new query finds its semantic
         * neighbours among them and inherits their judgements.
         *
         * Auditable to the row, which is the point: *"boosted because you marked it
         * Preferred for 'liability cap' on 12 Aug, similarity 0.91."*
         */
        source: z.literal('judged_query'),
        value: z.number(),
        detail: z.string().min(1).max(500),
        judged_query: z.string().min(1).max(400),
        judgement: SearchJudgementSchema,
        /** `sim(Q, j.query)`. Only judgements above τ (0.85) contribute. */
        similarity: UnitScoreSchema,
        judged_by_upn: UpnSchema,
        /** `1.0` current version, `0.3` if the clause has been revised since. */
        staleness_weight: z.number().min(0).max(1),
        judged_against_version_no: VersionNoSchema,
    }),
    z.object({
        /** Manual authority — a clause pinned or buried by review (WP-09 §8). */
        source: z.literal('manual_boost'),
        value: z.number(),
        detail: z.string().min(1).max(500),
        boost_weight: z.number().int(),
    }),
]);
/**
 * The full derivation of one result's score.
 *
 * `final_score = base_score + λ_f · feedback + λ_b · boost`, where `base_score` is
 * Reciprocal Rank Fusion of the two retrievers normalised to `[0, 1]`. RRF is used
 * rather than a weighted sum because it needs no tuning and is robust to the
 * retrievers scoring on different scales (WP-09 §5).
 */
export const ReasonTraceSchema = z.object({
    final_score: z.number(),
    /** RRF of the lexical and vector ranks, normalised to `[0, 1]`. */
    base_score: UnitScoreSchema,
    contributions: z.array(ScoreContributionSchema).min(1),
    /** Every alias applied to the query before retrieval, in order. */
    aliases_applied: z.array(z.object({
        alias_term: z.string().min(1).max(120),
        canonical_term: z.string().min(1).max(200),
    })),
});
/* -------------------------------------------------------------------------- */
/* Request                                                                     */
/* -------------------------------------------------------------------------- */
/**
 * Optional user-chosen narrowing.
 *
 * Read the name literally: these can only **narrow** the row set the server has
 * already decided the caller may see. They are intersected into the same `WHERE`
 * clause as the scope predicate — never substituted for it, never applied after
 * retrieval. Omitting all of them widens the result set to the caller's full
 * scope and no further.
 *
 * `party_key` is the one to be careful with. It is a *filter*, not a *grant*: a
 * caller naming a party outside their scope gets an empty result set, exactly as
 * if the party did not exist. It cannot be used to reach anything.
 */
export const SearchNarrowSchema = z
    .object({
    party_key: PartyKeySchema.optional(),
    contract_type: z.array(ContractTypeSchema).min(1).optional(),
    lifecycle_status: z.array(LifecycleStatusSchema).min(1).optional(),
    processing_status: z.array(ProcessingStatusSchema).min(1).optional(),
    /** Inclusive bounds on `effective_date`. */
    effective_from: IsoDateSchema.optional(),
    effective_to: IsoDateSchema.optional(),
    /** Inclusive bounds on `expiry_date` — the O-1 renewal-radar filter. */
    expiring_from: IsoDateSchema.optional(),
    expiring_to: IsoDateSchema.optional(),
    /** Restrict to the current version of each agreement family (§8.1). */
    current_version_only: z.boolean().optional(),
    /** Restrict to contracts with at least one unverified field (FR-07). */
    unverified_only: z.boolean().optional(),
    /** CLM-77 — all-of: a hit carries every listed tag (its own tags; not inherited). A predicate, never a rank change (FR-27). */
    tags: TagFilterSchema.optional(),
    /** CLM-87 — the PRIMARY party's region; anchored on root_contract_key like party_key. A predicate, never a rank change (FR-27). */
    region: RegionSchema.optional(),
    /** CLM-91 — the signing cloudEQ entity; equality on the hit's own contract.cloudeq_entity_key (D-91-5). A predicate, never a rank change (FR-27). */
    cloudeq_entity: CloudeqEntityKeySchema.optional(),
    /** CLM-97 (D-97-5) — exact match on the normalised `key_terms.payment_terms` facet value (e.g. `'Net 60'`), as lifted by the Ask intent pass or typed directly. A predicate, never a rank change (FR-27). */
    payment_terms: z.string().min(1).max(400).optional(),
    /** CLM-99 (D-99-7a) — exact match on the primary party's Dynamics billing client ID; anchored on root_contract_key like party_key. A predicate, never a rank change (FR-27). */
    billing_client_id: z.string().min(1).max(64).optional(),
})
    .strict();
/**
 * What to search over.
 *
 * `contracts` searches metadata **and** contract content: FR-20 is satisfied by
 * storing the converted document text in the authoritative index and full-text
 * indexing it, rather than delegating content search to SharePoint. That decision
 * exists because two search systems meant content results could not be
 * scope-filtered in the same query — a direct NFR-06 risk
 * (`requirements.md` change 2).
 *
 * `clauses` searches the standard clause library (FR-25).
 *
 * `ask` — CLM-97, R-97-1 — is natural-language search: countable facets over the
 * nine verified key-terms fields plus paragraph-level hybrid retrieval over
 * `contract_clause`, with a per-contract fallback to the `contracts` lexical arm
 * when that contract has not been segmented yet (D-97-1(a); `contract_clause` is
 * empty until CLM-100's corpus run). See `phase4-specs/CLM-97-ask-style-search.md`.
 */
export const SearchTargetSchema = z.enum(['contracts', 'clauses', 'ask']);
/* -------------------------------------------------------------------------- */
/* Ask — CLM-97, R-97-1                                                        */
/* -------------------------------------------------------------------------- */
/**
 * A structured filter the deterministic intent pass lifted out of the typed
 * query text (D-97-5's v1 rule set) — never a model, always shown to the user as
 * an editable/removable chip before the search runs (CLAUDE.md rule 14: natural
 * language is handled by rules the user can see and override, not by a model).
 * `matched_text` is the exact substring that triggered the rule, so the UI can
 * highlight it in the original query.
 */
export const LiftedFilterSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('party'),
        party_key: PartyKeySchema,
        display_name: z.string().min(1).max(200),
        matched_text: z.string().min(1).max(400),
    }),
    z.object({
        kind: z.literal('payment_terms'),
        /** Normalised facet value, e.g. `'Net 60'` — see `AskFacetSchema`. */
        value: z.string().min(1).max(400),
        matched_text: z.string().min(1).max(400),
    }),
    z.object({
        kind: z.literal('contract_type'),
        value: ContractTypeSchema,
        matched_text: z.string().min(1).max(400),
    }),
    z.object({
        kind: z.literal('date_window'),
        field: z.enum(['effective', 'expiring']),
        from: IsoDateSchema.nullable(),
        to: IsoDateSchema.nullable(),
        matched_text: z.string().min(1).max(400),
    }),
]);
/**
 * D-97-4 — one of the nine key-terms fields, as a distinct-value count over the
 * caller's scope + narrow (+ any lifted filters already applied). No numeric
 * parsing: `value` is the extracted string, normalised for grouping (trimmed,
 * whitespace-collapsed) but displayed as written. `verification_state` is
 * worst-case-wins across the contributing rows — a count is never shown as
 * settled fact when any contributor is not (rule 7).
 */
export const AskFacetValueSchema = z.object({
    value: z.string().min(1).max(400),
    count: z.number().int().min(1),
    /**
     * CLM-108 — `auto_approved` when every contributing row was settled by policy;
     * a mix of policy and human settlements stays `mixed`, never `verified`.
     */
    verification_state: z.enum(['verified', 'unverified', 'mixed', 'auto_approved']),
});
export const AskFacetSchema = z.object({
    /** One of the nine `key_terms` field names, dotted as `field_verification` names them. */
    field_name: z.string().min(1).max(80),
    values: z.array(AskFacetValueSchema),
});
/**
 * The passage behind an Ask hit. `kind: 'passage'` is a real paragraph from
 * `contract_clause`; `kind: 'lexical_fallback'` means this contract has not been
 * segmented yet (D-97-1(a)) and the highlight comes from the same whole-contract
 * lexical arm `target: 'contracts'` uses — never silently blended as if it were
 * the same thing, which is why the discriminant exists on the wire, not just in
 * a comment.
 */
export const AskPassageSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('passage'),
        html: z.string().min(1).max(2000),
        /** `contract_clause.paragraph_ref` — a position label, e.g. a clause/section number, when the document has one. */
        paragraph_ref: z.string().min(1).max(60).nullable(),
    }),
    z.object({
        kind: z.literal('lexical_fallback'),
        html: z.string().min(1).max(2000),
    }),
]);
export const AskSearchHitSchema = z.object({
    contract: ContractSummarySchema,
    passage: AskPassageSchema,
    reason_trace: ReasonTraceSchema.nullable(),
    tags: z.array(TagSchema),
});
export const SearchRequestSchema = z
    .object({
    /** Plain language. Users type how they think, not how a lawyer drafts. */
    /** Trimmed first: whitespace is not a query, and the contract should say so, not a handler. */
    q: z.string().trim().min(1).max(400),
    target: SearchTargetSchema,
    narrow: SearchNarrowSchema.optional(),
    page: PageRequestSchema.optional(),
    /**
     * Omit or set `false` to save payload on a list view. FR-27 requires the trace
     * to be *available* for every result, not that every caller fetch it.
     */
    include_reason_trace: z.boolean().optional(),
})
    .strict();
/* -------------------------------------------------------------------------- */
/* Response                                                                    */
/* -------------------------------------------------------------------------- */
export const ContractSearchHitSchema = z.object({
    contract: ContractSummarySchema,
    /**
     * Highlighted extracts from the matched text. Spans plus the surrounding
     * sentence, produced by the index — the front end must not re-derive them.
     */
    snippets: z.array(z.object({
        field_name: z.string().min(1).max(80),
        /** Highlight markers are `<mark>`…`</mark>`; the text is escaped. */
        html: z.string().min(1).max(2000),
    })),
    /** `null` when `include_reason_trace` was not requested. */
    reason_trace: ReasonTraceSchema.nullable(),
    /** CLM-77 — the contract's own tags, sorted (D-77-3: on the hit, not on `ContractSummary`). */
    tags: z.array(TagSchema),
});
export const ClauseSearchHitSchema = z.object({
    clause: LibraryClauseSchema,
    /** Contracts in scope that use this clause, for *"who else has this?"*. */
    in_scope_usage_count: z.number().int().min(0),
    reason_trace: ReasonTraceSchema.nullable(),
});
/**
 * Discriminated on `target` so a caller cannot read clause hits out of a contract
 * search without narrowing the union first.
 */
export const SearchResponseSchema = z.discriminatedUnion('target', [
    z.object({
        target: z.literal('contracts'),
        /** Echoed back after alias expansion, so the UI can show what was run. */
        effective_query: z.string().min(1).max(800),
        hits: z.array(ContractSearchHitSchema),
        page: PageInfoSchema,
        /** NFR-10 — measured server-side and logged (WP-09 §8 `search_log`). */
        latency_ms: z.number().int().min(0),
        /** Joins this search to its `audit_log` read event (NFR-07). */
        search_id: z.string().min(1).max(64),
    }),
    z.object({
        target: z.literal('clauses'),
        effective_query: z.string().min(1).max(800),
        hits: z.array(ClauseSearchHitSchema),
        page: PageInfoSchema,
        latency_ms: z.number().int().min(0),
        search_id: z.string().min(1).max(64),
    }),
    z.object({
        target: z.literal('ask'),
        effective_query: z.string().min(1).max(800),
        /** D-97-5 — what the intent pass lifted out of `q`, editable/removable by the caller on the next search. */
        lifted_filters: z.array(LiftedFilterSchema),
        /** D-97-4 — the countable-facts header, computed over scope + narrow + lifted filters. Always present; empty when nothing in scope has any value for a field. */
        facets: z.array(AskFacetSchema),
        hits: z.array(AskSearchHitSchema),
        page: PageInfoSchema,
        latency_ms: z.number().int().min(0),
        search_id: z.string().min(1).max(64),
    }),
]);
/**
 * Records which result the user actually opened — WP-09 §5 stage ⑥.
 *
 * An implicit signal, and the cheapest one available: *"shown but never
 * selected, repeatedly"* is how the weakness dashboard finds a clause ranked too
 * high. Keyed on `search_id` from the response so it joins to the logged query.
 */
export const SearchSelectionRequestSchema = z
    .object({
    search_id: z.string().min(1).max(64),
    selected_contract_key: ContractKeySchema.optional(),
    selected_clause_code: ClauseCodeSchema.optional(),
})
    .strict();
export const SearchSelectionResponseSchema = z.object({
    recorded: z.literal(true),
});
//# sourceMappingURL=search.js.map